import { useMemo } from 'react'
import clsx from 'clsx'
import { v4 as uuid4 } from 'uuid'

import { BuilderDroppableElement } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { toSdkPageBody } from '@/adapters/__project__/BuilderPageAdapter'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useDesignBlocksGenerator } from '@/hooks/useDesignBlocksGenerator'
import { useFormFieldsGenerator } from '@/hooks/useFormFieldsGenerator'
import { BuilderComponent } from '@/shared/builder'
import { flatten } from '@/shared/builder-navigator'
import { EVENT_KEYS } from '@/shared/custom-event'
import { ComponentIconMap, DesignBlocksIconMap, PagesIconMap } from '@/shared/icons'
import { parseCompSettings } from '@/utils/builder/comp-parsers'
import { generateSectionElements, generateTabItems } from '@/utils/builder/extra-parsers'

const getIcon = (compId: string, type: 'components' | 'design-blocks' | 'pages') => {
  switch (type) {
    case 'design-blocks':
      return DesignBlocksIconMap[compId]
    case 'pages':
      return PagesIconMap[compId]
    default:
      return ComponentIconMap[compId]
  }
}

interface DraggableComponentProps {
  type: 'components' | 'design-blocks' | 'pages'
  comp: BuilderComponent
  groupId: string
}

export function DraggableComponent({ type, comp, groupId }: DraggableComponentProps) {
  const { tables } = useDbClientContext()
  const {
    menus,
    setShowFabWarningModal,
    addBuilderComponent,
    appClient,
    placeholderParentId,
    setPlaceholderParentId,
    setFabWarningReason,
  } = useAppBuilderContext()
  const { activeLayer, setActiveComponent, activeLayerContainer, activeLayerClient, activeComponent } =
    useActiveLayerContext()

  const flattenedSettings = useMemo(() => {
    const list = [...(comp.settings.data ?? []), ...(comp.settings.design ?? [])]

    if (comp.settings.dataGroup) {
      const stgs = Object.values(comp.settings.dataGroup)
      for (const s of stgs) {
        if (Array.isArray(s.items)) {
          list.push(...s.items)
        }
      }
    }

    if (comp.settings.designGroup) {
      const stgs = Object.values(comp.settings.designGroup)
      for (const s of stgs) {
        if (Array.isArray(s.items)) {
          list.push(...s.items)
        }
      }
    }

    if (comp.settings.behavior?.custom) {
      const stgs = Object.values(comp.settings.behavior?.custom)
      for (const s of stgs) {
        if (Array.isArray(s.items)) {
          list.push(...s.items)
        }
      }
    }

    return list
  }, [comp])

  // TODO: ============================== DRAG & DROP ==================================//

  // const [collected, drag] = useDrag<BuilderDraggableElement>(() => {
  //   return {
  //     type: 'component',
  //     item: {
  //       id: uuid4(),
  //       icon: comp.icon,
  //       compId: comp.compId,
  //       groupId: groupId,
  //       title: comp.title,
  //       isDetailPageComponent: comp.isDetailPageComponent,
  //       inactive: false,
  //       settings: parseCompSettings(comp, { menus, tables, flattened: flattenedSettings }, activeLayerTableId),
  //       children: [],
  //       nested: comp.nested,
  //       isForm: comp.isForm,
  //       index: -1,
  //       parentId: null,
  //       parentTitle: null,
  //       parentType: null,
  //     } as BuilderDraggableElement,
  //     end: (item, monitor) => {
  //       const dropResult = monitor.getDropResult()
  //       if (dropResult) {
  //         //item.containerId = 'component-draggable'
  //         //item.index = pages[activePageIndex].elements.length
  //       }
  //     },
  //     collect: (monitor) => {
  //       return {
  //         isDragging: monitor.isDragging(),
  //       }
  //     },
  //   }
  // }, [activeElements.length, activeLayerTableId, menus, flattenedSettings, tables])

  // const { isDragging } = collected as { isDragging: boolean }

  // const style = {
  //   zIndex: isDragging ? '10000' : undefined,
  // } as CSSProperties

  // TODO: ==============================================================================//

  const flattenedComps = activeLayerContainer?.elements ? flatten(activeLayerContainer.elements) : []

  // Access form generator
  const { generateFormFields } = useFormFieldsGenerator()

  // Access design blocks generator
  const { generateDesignBlocks } = useDesignBlocksGenerator()

  // Disable Globals Header/Footer if already present in project-level globals
  const globals = (appClient?.builderSettings as any)?._Globals as any
  const hasGlobalType = (typeId: 'header' | 'footer') => {
    const bodies: any[] = (globals?._Body || []) as any[]
    return bodies.some((b) => b?._GroupID === 'globals' && b?._ComponentID === typeId)
  }
  const isGlobalsGroup = groupId === 'globals'
  const isHeaderFooter = comp.compId === 'header' || comp.compId === 'footer'
  const isDisabled = isGlobalsGroup && isHeaderFooter && hasGlobalType(comp.compId as 'header' | 'footer')

  /**
   * Add element handler
   */
  const handleAddElement = async () => {
    if (isDisabled) return
    // TODO: handle pages
    if (type === 'pages') {
      return
    }

    if (!activeLayerClient) return

    if (type === 'design-blocks') {
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: true } }))
    }

    // Handle FAB restrictions
    if (comp.compId === 'fab') {
      // FAB not allowed in overlay
      if (activeLayer.type === 'overlay') return

      // in page, only one FAB allowed
      const alreadyHasFab = flattenedComps.some((el) => el.compId === 'fab')
      if (alreadyHasFab) {
        setFabWarningReason('limit')
        setShowFabWarningModal(true)
        return
      }

      // prevent inserting FAB inside Global Header via insert button
      const targetParentId =
        placeholderParentId || (activeComponent?.groupId === 'globals' ? activeComponent.id : undefined)
      if (targetParentId) {
        const globals = ((appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
        const hasAncestor = (bodies: any[], targetId: string, predicate: (n: any) => boolean): boolean => {
          const walk = (nodes: any[], stack: any[] = []): boolean => {
            for (const n of nodes) {
              const newStack = [...stack, n]
              if (n._id === targetId) {
                return newStack.some((p) => p?._GroupID === 'globals' && p?._ComponentID === 'header')
              }
              if (Array.isArray(n._Body) && n._Body.length) {
                if (walk(n._Body, newStack)) return true
              }
            }
            return false
          }
          return walk(bodies)
        }
        if (
          hasAncestor(
            globals._Body || [],
            targetParentId,
            (n) => n?._GroupID === 'globals' && n?._ComponentID === 'header'
          )
        ) {
          setFabWarningReason('header')
          setShowFabWarningModal(true)
          return
        }

        // If inserting into Global Footer, prevent multiple FABs under the same footer
        const findNode = (bodies: any[], targetId: string): any | undefined => {
          for (const n of bodies) {
            if (n._id === targetId) return n
            if (Array.isArray(n._Body) && n._Body.length) {
              const found = findNode(n._Body, targetId)
              if (found) return found
            }
          }
          return undefined
        }
        const findAncestorFooter = (bodies: any[], targetId: string): any | undefined => {
          const walk = (nodes: any[], stack: any[] = []): any | undefined => {
            for (const n of nodes) {
              const newStack = [...stack, n]
              if (n._id === targetId) {
                return newStack.reverse().find((p) => p?._GroupID === 'globals' && p?._ComponentID === 'footer')
              }
              if (Array.isArray(n._Body) && n._Body.length) {
                const found = walk(n._Body, newStack)
                if (found) return found
              }
            }
            return undefined
          }
          return walk(bodies)
        }
        const footerAncestor = findAncestorFooter(globals._Body || [], targetParentId)
        if (footerAncestor) {
          const countFabInSubtree = (node: any): number => {
            let count = node?._ComponentID === 'fab' ? 1 : 0
            if (Array.isArray(node?._Body) && node._Body.length) {
              for (const child of node._Body) {
                count += countFabInSubtree(child)
              }
            }
            return count
          }
          const existingFabCount = countFabInSubtree(footerAncestor)
          if (existingFabCount >= 1) {
            setFabWarningReason('limit')
            setShowFabWarningModal(true)
            return
          }
        }
      }
    }

    const id = uuid4()
    let newEl: BuilderDroppableElement = {
      id,
      compId: comp.compId,
      groupId: groupId,
      title: comp.title,
      icon: comp.icon,
      inactive: false,
      nested: comp.nested,
      isForm: comp.isForm,
      isDetailPageComponent: comp.isDetailPageComponent,
      children: [],
      parentId: null,
      parentTitle: null,
      parentType: null,
    }

    const layer = activeLayerClient
    const detailTableId = layer.settings._TableID

    let settings: Record<string, any> = {}
    let childSettings: Record<string, Record<string, any>> = {}
    let designBlockChildren = []

    // Handle form elements
    if (comp.isForm) {
      const formEl = flattenedComps.find((fi) => fi.compId === 'form')
      if (formEl) {
        newEl.parentId = formEl.id
        newEl.parentTitle = formEl.title
        newEl.parentType = formEl.compId
      }
    }

    switch (true) {
      case comp.compId === 'section': {
        // generate settings
        settings = parseCompSettings(
          comp,
          { menus, tables, flattened: flattenedSettings },
          detailTableId,
          newEl.parentId ?? ''
        )

        // generate children
        const { children, childElSettings } = generateSectionElements({
          parent: newEl,
          settings,
          ctx: { tables, menus, layer },
        })
        newEl.children = children
        childSettings = childElSettings
        break

        break
      }
      case comp.compId === 'tabs': {
        // generate settings
        settings = parseCompSettings(
          comp,
          { menus, tables, flattened: flattenedSettings },
          detailTableId,
          newEl.parentId ?? ''
        )

        // generate tab items
        const { children, childElSettings } = generateTabItems({
          parent: newEl,
          settings,
          ctx: { tables, menus, layer },
        })
        newEl.children = children
        childSettings = childElSettings
        break
      }
      case comp.compId === 'form': {
        // generate form settings
        settings = parseCompSettings(
          comp,
          { menus, tables, flattened: flattenedSettings, layer },
          detailTableId,
          newEl.parentId ?? ''
        )
        // generate children and childSettings
        const { children, childElSettings } = generateFormFields(newEl.id, settings, detailTableId)
        newEl.children = children
        childSettings = childElSettings
        break
      }
      case comp.isDesignBlock: {
        // generate design block settings
        const { children, childElSettings } = await generateDesignBlocks(newEl.id, comp.compId)
        designBlockChildren = children
        childSettings = childElSettings
        break
      }
      default: {
        // generate settings
        settings = parseCompSettings(
          comp,
          { menus, tables, flattened: flattenedSettings, layer },
          detailTableId,
          newEl.parentId ?? ''
        )
      }
    }

    // Handle Globals group: merge into app-level _Globals (full replace payload), trigger page update, then return
    if (groupId === 'globals') {
      const elBody = toSdkPageBody([newEl])
      const flatSettings = { [elBody[0]._id]: settings, ...childSettings }
      const prevGlobals = ((appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
      const prevBody: any[] = Array.isArray(prevGlobals._Body) ? prevGlobals._Body : []
      const prevSettings: Record<string, any> = prevGlobals._BodySettings || {}

      const incomingTypes = new Set(elBody.map((b) => b._ComponentID))

      // Remove previous bodies of the same type(s)
      const keptPrevBodies = prevBody.filter((b: any) => !incomingTypes.has(b._ComponentID))

      // Clean settings for removed previous bodies
      const collectIds = (bodies: any[]): string[] => {
        const ids: string[] = []
        const walk = (arr: any[]) => {
          for (const n of arr) {
            ids.push(n._id)
            if (Array.isArray(n._Body) && n._Body.length) walk(n._Body)
          }
        }
        walk(bodies)
        return ids
      }
      const removedPrevBodies = prevBody.filter((b: any) => incomingTypes.has(b._ComponentID))
      const idsToRemove = collectIds(removedPrevBodies)
      const cleanedPrevSettings = { ...prevSettings }
      for (const rid of idsToRemove) {
        delete cleanedPrevSettings[rid]
      }

      const mergedBody = [...keptPrevBodies, ...elBody]
      const mergedSettings = { ...cleanedPrevSettings, ...flatSettings }

      await appClient.UpdateGlobals({ _Body: mergedBody, _BodySettings: mergedSettings })
      return
    }

    window.prettyLog('[Builder] Create new component', {
      newEl,
      settings,
      childSettings,
    })

    if (!comp.isDesignBlock) {
      setActiveComponent({
        layerType: activeLayer.type,
        id,
        compId: comp.compId,
        groupId,
      })
    }

    if (comp.isDesignBlock) {
      const parentBody = designBlockChildren[0]
      const designBlocksSettings: any = childSettings[parentBody._id]
      // If inserting inside a globals subtree, update _Globals instead of page
      const targetParentId =
        placeholderParentId || (activeComponent?.groupId === 'globals' ? activeComponent.id : undefined)

      if (targetParentId) {
        const globals = ((appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
        const containsInBodies = (bodies: any[], targetId: string): boolean => {
          for (const b of bodies) {
            if (b._id === targetId) return true
            if (Array.isArray(b._Body) && b._Body.length && containsInBodies(b._Body, targetId)) return true
          }
          return false
        }
        if (containsInBodies(globals._Body || [], targetParentId)) {
          const insertInto = (bodies: any[], targetId: string, child: any): any[] => {
            return bodies.map((b) => {
              if (b._id === targetId) return { ...b, _Body: [...b._Body, child] }
              if (Array.isArray(b._Body) && b._Body.length) return { ...b, _Body: insertInto(b._Body, targetId, child) }
              return b
            })
          }
          const updatedBody = insertInto(globals._Body || [], targetParentId, parentBody)
          const flatSettings = { [parentBody._id]: designBlocksSettings, ...childSettings }

          await appClient.UpdateGlobals({
            _Body: updatedBody,
            _BodySettings: { ...globals._BodySettings, ...flatSettings },
          })
          setPlaceholderParentId(null)
          window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: false } }))
          return
        }
      }

      await addBuilderComponent(activeLayerClient, parentBody, designBlocksSettings, childSettings)
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.GLOBAL_APP_LOADING, { detail: { loading: false } }))
    } else {
      const elBody = toSdkPageBody([newEl])
      // If inserting inside a globals subtree, update _Globals instead of page
      const targetParentId =
        placeholderParentId || (activeComponent?.groupId === 'globals' ? activeComponent.id : undefined)

      if (targetParentId) {
        const globals = ((appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
        const containsInBodies = (bodies: any[], targetId: string): boolean => {
          for (const b of bodies) {
            if (b._id === targetId) return true
            if (Array.isArray(b._Body) && b._Body.length && containsInBodies(b._Body, targetId)) return true
          }
          return false
        }
        if (containsInBodies(globals._Body || [], targetParentId)) {
          const insertInto = (bodies: any[], targetId: string, childArr: any[]): any[] => {
            return bodies.map((b) => {
              if (b._id === targetId) return { ...b, _Body: [...b._Body, childArr[0]] }
              if (Array.isArray(b._Body) && b._Body.length)
                return { ...b, _Body: insertInto(b._Body, targetId, childArr) }
              return b
            })
          }
          const updatedBody = insertInto(globals._Body || [], targetParentId, elBody)
          const flatSettings = { [elBody[0]._id]: settings, ...childSettings }
          await appClient.UpdateGlobals({
            _Body: updatedBody,
            _BodySettings: { ...globals._BodySettings, ...flatSettings },
          })
          setPlaceholderParentId(null)
          return
        }
      }

      addBuilderComponent(activeLayerClient, elBody[0], settings, childSettings, newEl.parentId ?? '')
    }
  }

  return (
    <div
      id={`comp_${comp.compId}`}
      // ref={drag}
      // style={style}
      onClick={isDisabled ? undefined : handleAddElement}
      className={clsx(
        'mk-comp px-1!',
        'flex-1 min-w-[100px] aspect-[4/3] shrink-0',
        'p-4 rounded-md flex-col',
        isDisabled
          ? 'bg-zinc-200/60 dark:bg-slate-600/60 opacity-50 cursor-not-allowed'
          : 'bg-zinc-200 dark:bg-slate-600',
        !isDisabled && 'hover:bg-primary-500 dark:hover:bg-primary-500',
        !isDisabled && 'hover:text-white transition-colors',
        'flex items-center justify-center gap-2'
        // isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'
      )}
    >
      <div>
        <MaterialIcon icon={getIcon(comp.compId, type)} size={28} />
      </div>
      <div className='txt text-center text-wrap whitespace-normal! text-m-sm'>{comp.title}</div>
    </div>
  )
}
